import { useEffect } from 'react';
import { Alert, Col, Row } from 'react-bootstrap';
import { useParams, useSearchParams } from 'react-router-dom';
import BackLinkCount from '../components/BackLinkCount';
import DomainCount from '../components/DomainCount';
import WorldMapCard from '../components/WorldMapCard';
import MarketingExpenses from '../components/marketing-expenses/MarketingExpenses';
import SpamScoreDistribution from '../components/SpamScoreDistribution';
import MostClickedDomainsTable from '../components/MostClickedDomainsTable';
import AnchorTextTable from '../components/AnchorTextTable';
import BestLinksTable from '../components/BestLinksTable';
import ReferringIpTable from '../components/ReferringIpTable';
import DomainAuthorityChart from '../components/DomainAuthorityChart';
import DiscoveryTrendCharts from '../components/DiscoveryTrendCharts';
import {
  useGetAnchorTextsAndNumOfLinksQuery,
  useGetMozDomainAuthorityQuery,
  useGetMozSpamScoreQuery,
  useGetNumOfBacklinkQuery,
  useGetNumOfDomainsQuery,
  useGetNumOfReferralDomainsCountryNameQuery,
  useGetNumOfReferralDomainsTldHostNameQuery,
  useGetPagesAndNumOfLinksQuery,
  useGetReferralDomainsAndNumOfLinksQuery
} from '../api/backlinkOverviewApi';
import { useGetNumOfBacklinksPerMonthQuery } from '../api/backlinkBacklinksApi';
import { useGetBacklinksCountQuery } from '../api/backlinkBacklinksApi';
import { useGetNumOfReferralDomainsPerMonthQuery } from '../api/backlinkReferralDomainApi';
import { useGetReferringIpsCountQuery } from '../api/backlinkReferringIpApi';
import useBacklinkQueryBody, {
  toOverviewQueryBody
} from '../hooks/useBacklinkQueryBody';
import FiltersComponent from '@/app/common/components/filters/FiltersComponent';
import { ReferralPagesColumns } from '../columnsConfig';
import BacklinkTabStats from '../components/BacklinkTabStats';
import ApiLoadError from '@/components/errors/ApiLoadError';
import DataLoadError from '@/components/errors/DataLoadError';
import { getActiveQueryError } from '@/app/common/utils/apiErrorUtils';

const BacklinkApiError = ({
  isError,
  error,
  refetch,
  isFetching,
  compact = false
}: Record<string, any>) => (
  <ApiLoadError
    error={error}
    isError={isError}
    onRetry={() => refetch?.()}
    isRetrying={isFetching}
    compact={compact}
  />
);

const Overview = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { domain: domainName } = useParams();
  const body = useBacklinkQueryBody();
  const overviewBody = toOverviewQueryBody(body);

  useEffect(() => {
    if (searchParams.get('grouping')) return;
    const params = new URLSearchParams(searchParams);
    params.set('grouping', 'domain');
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleGroupingChange = (grouping: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('grouping', grouping);
    setSearchParams(params, { replace: true });
  };
  const {
    data: numOfBacklinkRes,
    isFetching: numOfBacklinkLoading,
    isError: numOfBacklinkError,
    error: numOfBacklinkErrorDetails,
    refetch: refetchNumOfBacklink
  } = useGetNumOfBacklinkQuery(overviewBody);
  const {
    data: getNumOfBacklinksPerMonthRes,
    isFetching: getNumOfBacklinksPerMonthLoading,
    isError: getNumOfBacklinksPerMonthError,
    error: getNumOfBacklinksPerMonthErrorDetails,
    refetch: refetchNumOfBacklinksPerMonth
  } = useGetNumOfBacklinksPerMonthQuery({ ...overviewBody, limit: 100 });
  const {
    data: backlinkTabStatsRes,
    isFetching: backlinkTabStatsLoading,
    isError: backlinkTabStatsError,
    error: backlinkTabStatsErrorDetails,
    refetch: refetchBacklinkTabStats
  } = useGetBacklinksCountQuery({
    ...overviewBody,
    page: 1,
    perPage: 1
  });
  const {
    data: numOfDomainsRes,
    isFetching: numOfDomainsLoading,
    isError: numOfDomainsError,
    error: numOfDomainsErrorDetails,
    refetch: refetchNumOfDomains
  } = useGetNumOfDomainsQuery(overviewBody);
  const {
    data: numOfReferralDomainsPerMonthRes,
    isFetching: numOfReferralDomainsPerMonthLoading,
    isError: numOfReferralDomainsPerMonthError,
    error: numOfReferralDomainsPerMonthErrorDetails,
    refetch: refetchNumOfReferralDomainsPerMonth
  } = useGetNumOfReferralDomainsPerMonthQuery({ ...overviewBody, limit: 100 });
  const {
    data: numOfReferralDomainsCountryNameRes,
    isFetching: numOfReferralDomainsCountryNameLoading,
    isError: numOfReferralDomainsCountryNameError,
    error: numOfReferralDomainsCountryNameErrorDetails,
    refetch: refetchNumOfReferralDomainsCountryName
  } = useGetNumOfReferralDomainsCountryNameQuery(overviewBody);
  const {
    data: numOfReferralDomainsDomainsTldHostNameRes,
    isFetching: numOfReferralDomainsDomainsTldHostNameLoading,
    isError: numOfReferralDomainsDomainsTldHostNameError,
    error: numOfReferralDomainsDomainsTldHostNameErrorDetails,
    refetch: refetchNumOfReferralDomainsDomainsTldHostName
  } = useGetNumOfReferralDomainsTldHostNameQuery(overviewBody);
  const {
    data: mozDomainAuthorityRes,
    isFetching: mozDomainAuthorityLoading,
    isError: mozDomainAuthorityError,
    error: mozDomainAuthorityErrorDetails,
    refetch: refetchMozDomainAuthority
  } = useGetMozDomainAuthorityQuery(overviewBody);
  const {
    data: mozSpamScoreRes,
    isFetching: mozSpamScoreLoading,
    isError: mozSpamScoreError,
    error: mozSpamScoreErrorDetails,
    refetch: refetchMozSpamScore
  } = useGetMozSpamScoreQuery(overviewBody);
  const {
    data: referralDomainsAndNumOfLinksRes,
    isFetching: referralDomainsAndNumOfLinksLoading,
    isError: referralDomainsAndNumOfLinksError,
    error: referralDomainsAndNumOfLinksErrorDetails,
    refetch: refetchReferralDomainsAndNumOfLinks
  } = useGetReferralDomainsAndNumOfLinksQuery(overviewBody);
  const {
    data: anchorTextsAndNumOfLinksRes,
    isFetching: anchorTextsAndNumOfLinksLoading,
    isError: anchorTextsAndNumOfLinksError,
    error: anchorTextsAndNumOfLinksErrorDetails,
    refetch: refetchAnchorTextsAndNumOfLinks
  } = useGetAnchorTextsAndNumOfLinksQuery(overviewBody);
  const {
    data: pagesAndNumOfLinksRes,
    isFetching: pagesAndNumOfLinksLoading,
    isError: pagesAndNumOfLinksError,
    error: pagesAndNumOfLinksErrorDetails,
    refetch: refetchPagesAndNumOfLinks
  } = useGetPagesAndNumOfLinksQuery(overviewBody);
  const {
    data: referringIpsRes,
    isFetching: referringIpsLoading,
    isError: referringIpsError,
    error: referringIpsErrorDetails,
    refetch: refetchReferringIps
  } = useGetReferringIpsCountQuery({
    ...overviewBody,
    page: 1,
    perPage: 10,
    sort: { column: 'distinct_host_from_count', order: 'desc' }
  });

  const trendError = getActiveQueryError([
    {
      isError: getNumOfBacklinksPerMonthError,
      error: getNumOfBacklinksPerMonthErrorDetails,
      refetch: refetchNumOfBacklinksPerMonth,
      isFetching: getNumOfBacklinksPerMonthLoading
    },
    {
      isError: numOfReferralDomainsPerMonthError,
      error: numOfReferralDomainsPerMonthErrorDetails,
      refetch: refetchNumOfReferralDomainsPerMonth,
      isFetching: numOfReferralDomainsPerMonthLoading
    }
  ]);
  const hasNoBacklinks =
    !numOfBacklinkLoading &&
    !numOfBacklinkError &&
    numOfBacklinkRes?.data?.value === 0;

  return (
    <>
      <FiltersComponent
        columns={ReferralPagesColumns}
        preQueries={searchParams.toString()}
        groupingMode={body.grouping}
        onGroupingModeChange={handleGroupingChange}
      />
      {hasNoBacklinks && (
        <Alert variant="warning" className="mb-3 fs--1">
          برای این دامنه{' '}
          {domainName && <span dir="ltr">({domainName})</span>} هیچ بک‌لینکی
          شناسایی نشد. لطفاً دامنه دیگری را بررسی کنید.
        </Alert>
      )}
      <Row className="g-3 mb-3 align-items-stretch">
        <Col lg={8} className="d-flex">
          <div className="d-flex flex-column flex-grow-1 w-100">
            {!(
              getNumOfBacklinksPerMonthLoading ||
              numOfReferralDomainsPerMonthLoading
            ) && trendError ? (
              <DataLoadError {...trendError} />
            ) : (
              <DiscoveryTrendCharts
                loading={
                  getNumOfBacklinksPerMonthLoading ||
                  numOfReferralDomainsPerMonthLoading
                }
                backlinkData={getNumOfBacklinksPerMonthRes?.data}
                domainData={numOfReferralDomainsPerMonthRes?.data}
              />
            )}
          </div>
        </Col>
        <Col lg={4} className="d-flex">
          <div className="d-flex flex-column gap-3 flex-grow-1 w-100">
            <Row className="g-3 flex-shrink-0">
              <Col xs={6}>
                {!numOfBacklinkLoading && numOfBacklinkError ? (
                  <BacklinkApiError
                    isError={numOfBacklinkError}
                    error={numOfBacklinkErrorDetails}
                    refetch={refetchNumOfBacklink}
                    isFetching={numOfBacklinkLoading}
                    compact
                  />
                ) : (
                  <BackLinkCount
                    loading={numOfBacklinkLoading}
                    count={numOfBacklinkRes?.data?.value}
                  />
                )}
              </Col>
              <Col xs={6}>
                {!numOfDomainsLoading && numOfDomainsError ? (
                  <BacklinkApiError
                    isError={numOfDomainsError}
                    error={numOfDomainsErrorDetails}
                    refetch={refetchNumOfDomains}
                    isFetching={numOfDomainsLoading}
                    compact
                  />
                ) : (
                  <DomainCount
                    loading={numOfDomainsLoading}
                    count={numOfDomainsRes?.data?.value}
                  />
                )}
              </Col>
            </Row>
            <div className="flex-grow-1 d-flex" style={{ minHeight: 0 }}>
              {!backlinkTabStatsLoading && backlinkTabStatsError ? (
                <BacklinkApiError
                  isError={backlinkTabStatsError}
                  error={backlinkTabStatsErrorDetails}
                  refetch={refetchBacklinkTabStats}
                  isFetching={backlinkTabStatsLoading}
                />
              ) : (
                <BacklinkTabStats
                  loading={backlinkTabStatsLoading}
                  counts={backlinkTabStatsRes?.data?.counts}
                />
              )}
            </div>
          </div>
        </Col>
      </Row>
      <Row className="g-3 mb-3">
        <Col lg={8}>
          {!numOfReferralDomainsCountryNameLoading &&
          numOfReferralDomainsCountryNameError ? (
            <BacklinkApiError
              isError={numOfReferralDomainsCountryNameError}
              error={numOfReferralDomainsCountryNameErrorDetails}
              refetch={refetchNumOfReferralDomainsCountryName}
              isFetching={numOfReferralDomainsCountryNameLoading}
            />
          ) : (
            <WorldMapCard
              loading={numOfReferralDomainsCountryNameLoading}
              data={numOfReferralDomainsCountryNameRes?.data}
            />
          )}
        </Col>
        <Col lg={4}>
          {!numOfReferralDomainsDomainsTldHostNameLoading &&
          numOfReferralDomainsDomainsTldHostNameError ? (
            <BacklinkApiError
              isError={numOfReferralDomainsDomainsTldHostNameError}
              error={numOfReferralDomainsDomainsTldHostNameErrorDetails}
              refetch={refetchNumOfReferralDomainsDomainsTldHostName}
              isFetching={numOfReferralDomainsDomainsTldHostNameLoading}
            />
          ) : (
            <MarketingExpenses
              loading={numOfReferralDomainsDomainsTldHostNameLoading}
              listData={{
                tld: numOfReferralDomainsDomainsTldHostNameRes?.data?.tld,
                language:
                  numOfReferralDomainsDomainsTldHostNameRes?.data?.language
              }}
            />
          )}
        </Col>
      </Row>
      <Row className="g-3 mb-3">
        <Col md={6}>
          {!mozDomainAuthorityLoading && mozDomainAuthorityError ? (
            <BacklinkApiError
              isError={mozDomainAuthorityError}
              error={mozDomainAuthorityErrorDetails}
              refetch={refetchMozDomainAuthority}
              isFetching={mozDomainAuthorityLoading}
            />
          ) : (
            <DomainAuthorityChart
              loading={mozDomainAuthorityLoading}
              data={mozDomainAuthorityRes?.data}
              style={{ height: '300px' }}
            />
          )}
        </Col>
        <Col md={6}>
          {!mozSpamScoreLoading && mozSpamScoreError ? (
            <BacklinkApiError
              isError={mozSpamScoreError}
              error={mozSpamScoreErrorDetails}
              refetch={refetchMozSpamScore}
              isFetching={mozSpamScoreLoading}
            />
          ) : (
            <SpamScoreDistribution
              loading={mozSpamScoreLoading}
              data={mozSpamScoreRes?.data}
            />
          )}
        </Col>
      </Row>
      <Row className="g-3 mb-3">
        <Col md={6}>
          {!referralDomainsAndNumOfLinksLoading &&
          referralDomainsAndNumOfLinksError ? (
            <BacklinkApiError
              isError={referralDomainsAndNumOfLinksError}
              error={referralDomainsAndNumOfLinksErrorDetails}
              refetch={refetchReferralDomainsAndNumOfLinks}
              isFetching={referralDomainsAndNumOfLinksLoading}
            />
          ) : (
            <MostClickedDomainsTable
              loading={referralDomainsAndNumOfLinksLoading}
              data={referralDomainsAndNumOfLinksRes?.data}
            />
          )}
        </Col>
        <Col md={6}>
          {!anchorTextsAndNumOfLinksLoading &&
          anchorTextsAndNumOfLinksError ? (
            <BacklinkApiError
              isError={anchorTextsAndNumOfLinksError}
              error={anchorTextsAndNumOfLinksErrorDetails}
              refetch={refetchAnchorTextsAndNumOfLinks}
              isFetching={anchorTextsAndNumOfLinksLoading}
            />
          ) : (
            <AnchorTextTable
              loading={anchorTextsAndNumOfLinksLoading}
              data={anchorTextsAndNumOfLinksRes?.data}
            />
          )}
        </Col>
        <Col md={6}>
          {!pagesAndNumOfLinksLoading && pagesAndNumOfLinksError ? (
            <BacklinkApiError
              isError={pagesAndNumOfLinksError}
              error={pagesAndNumOfLinksErrorDetails}
              refetch={refetchPagesAndNumOfLinks}
              isFetching={pagesAndNumOfLinksLoading}
            />
          ) : (
            <BestLinksTable
              loading={pagesAndNumOfLinksLoading}
              data={pagesAndNumOfLinksRes?.data}
            />
          )}
        </Col>
        <Col md={6}>
          {!referringIpsLoading && referringIpsError ? (
            <BacklinkApiError
              isError={referringIpsError}
              error={referringIpsErrorDetails}
              refetch={refetchReferringIps}
              isFetching={referringIpsLoading}
            />
          ) : (
            <ReferringIpTable
              loading={referringIpsLoading}
              data={referringIpsRes?.data?.rows || []}
            />
          )}
        </Col>
      </Row>
    </>
  );
};

export default Overview;
